import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { requestInstallPrompt } from "@/lib/installPromptBus";
import { maybeAskForPush } from "@/lib/pushSoftAsk";
import { enqueue, isNetworkError } from "@/lib/offlineQueue";

const FIRST_TRACK_FLAG = "atp:first-track-prompted";

export interface SavedTopic {
  id: string;
  query: string;
  notify_email: boolean;
  is_active: boolean;
  match_count: number;
  last_matched_at: string | null;
  created_at: string;
}

export function useSavedTopics() {
  const { user } = useAuth();
  const [topics, setTopics] = useState<SavedTopic[]>([]);
  const [savedQueries, setSavedQueries] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setTopics([]);
      setSavedQueries(new Set());
      setLoading(false);
      return;
    }
    supabase
      .from("saved_topics")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .then(({ data }) => {
        if (data) {
          setTopics(data as SavedTopic[]);
          setSavedQueries(new Set(data.map((t) => t.query.toLowerCase().trim())));
        }
        setLoading(false);
      });
  }, [user]);

  // All mutations below paint the change locally first, then sync with the
  // database. If the DB rejects the write, we roll the UI back and surface a
  // toast so the user isn't left with a phantom state.
  const saveTopic = useCallback(
    async (query: string) => {
      if (!user) return false;
      const normalized = query.trim();
      const normalizedKey = normalized.toLowerCase();
      if (savedQueries.has(normalizedKey)) return false;

      // Optimistic insert with a temp row; replaced by the real row on success.
      const tempId = `temp-${crypto.randomUUID?.() ?? Math.random().toString(36).slice(2)}`;
      const tempRow: SavedTopic = {
        id: tempId,
        query: normalized,
        notify_email: true,
        is_active: true,
        match_count: 0,
        last_matched_at: null,
        created_at: new Date().toISOString(),
      };
      setTopics((prev) => [tempRow, ...prev]);
      setSavedQueries((prev) => new Set(prev).add(normalizedKey));

      const { data, error } = await supabase
        .from("saved_topics")
        .insert({ user_id: user.id, query: normalized })
        .select()
        .single();

      if (error) {
        if (isNetworkError(error) || !navigator.onLine) {
          await enqueue({
            kind: "saved_topic",
            payload: { op: "create", user_id: user.id, query: normalized },
            dedupKey: `topic-create:${user.id}:${normalizedKey}`,
          });
          toast.message("Tracking offline — will sync");
          return true;
        }
        console.error("Failed to save topic:", error);
        setTopics((prev) => prev.filter((t) => t.id !== tempId));
        setSavedQueries((prev) => {
          const next = new Set(prev);
          next.delete(normalizedKey);
          return next;
        });
        toast.error("Couldn't track that topic.");
        return false;
      }

      setTopics((prev) => prev.map((t) => (t.id === tempId ? (data as SavedTopic) : t)));
      // First topic tracked is the strongest install-prompt signal we have.
      if (typeof window !== "undefined" && !localStorage.getItem(FIRST_TRACK_FLAG)) {
        try { localStorage.setItem(FIRST_TRACK_FLAG, "1"); } catch { /* storage full */ }
        setTimeout(() => requestInstallPrompt("first-track"), 800);
        setTimeout(() => maybeAskForPush("first-track"), 3000);
      }
      return true;
    },
    [user, savedQueries]
  );

  const removeTopic = useCallback(
    async (topicId: string) => {
      if (!user) return;
      const topic = topics.find((t) => t.id === topicId);
      if (!topic) return;
      const normalizedKey = topic.query.toLowerCase().trim();

      // Optimistic remove.
      setTopics((prev) => prev.filter((t) => t.id !== topicId));
      setSavedQueries((prev) => {
        const next = new Set(prev);
        next.delete(normalizedKey);
        return next;
      });

      const { error } = await supabase.from("saved_topics").delete().eq("id", topicId);
      if (error) {
        if (isNetworkError(error) || !navigator.onLine) {
          await enqueue({
            kind: "saved_topic",
            payload: { op: "delete", user_id: user.id, topic_id: topicId },
            dedupKey: `topic-delete:${topicId}`,
          });
          return;
        }
        console.error("Failed to remove topic:", error);
        setTopics((prev) => [topic, ...prev]);
        setSavedQueries((prev) => new Set(prev).add(normalizedKey));
        toast.error("Couldn't untrack that topic.");
      }
    },
    [user, topics]
  );

  const toggleNotify = useCallback(
    async (topicId: string) => {
      if (!user) return;
      const topic = topics.find((t) => t.id === topicId);
      if (!topic) return;
      const newVal = !topic.notify_email;

      setTopics((prev) =>
        prev.map((t) => (t.id === topicId ? { ...t, notify_email: newVal } : t))
      );

      const { error } = await supabase
        .from("saved_topics")
        .update({ notify_email: newVal })
        .eq("id", topicId);
      if (error) {
        setTopics((prev) =>
          prev.map((t) => (t.id === topicId ? { ...t, notify_email: !newVal } : t))
        );
        toast.error("Couldn't update email notifications.");
      }
    },
    [user, topics]
  );

  const toggleActive = useCallback(
    async (topicId: string) => {
      if (!user) return;
      const topic = topics.find((t) => t.id === topicId);
      if (!topic) return;
      const newVal = !topic.is_active;

      setTopics((prev) =>
        prev.map((t) => (t.id === topicId ? { ...t, is_active: newVal } : t))
      );

      const { error } = await supabase
        .from("saved_topics")
        .update({ is_active: newVal })
        .eq("id", topicId);
      if (error) {
        setTopics((prev) =>
          prev.map((t) => (t.id === topicId ? { ...t, is_active: !newVal } : t))
        );
        toast.error("Couldn't toggle tracking.");
      }
    },
    [user, topics]
  );

  const updateQuery = useCallback(
    async (topicId: string, newQuery: string) => {
      if (!user) return false;
      const normalized = newQuery.trim();
      if (!normalized) return false;
      const topic = topics.find((t) => t.id === topicId);
      if (!topic) return false;

      // Check for duplicates (excluding current topic)
      const otherQueries = topics
        .filter((t) => t.id !== topicId)
        .map((t) => t.query.toLowerCase().trim());
      if (otherQueries.includes(normalized.toLowerCase())) return false;

      const { error } = await supabase
        .from("saved_topics")
        .update({ query: normalized })
        .eq("id", topicId);

      if (error) {
        console.error("Failed to update topic query:", error);
        return false;
      }

      // Update local state
      setSavedQueries((prev) => {
        const next = new Set(prev);
        next.delete(topic.query.toLowerCase().trim());
        next.add(normalized.toLowerCase());
        return next;
      });
      setTopics((prev) =>
        prev.map((t) => (t.id === topicId ? { ...t, query: normalized } : t))
      );
      return true;
    },
    [user, topics]
  );

  return { topics, savedQueries, loading, saveTopic, removeTopic, toggleNotify, toggleActive, updateQuery };
}
